import { createContext, useContext, useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Logo } from "@/components/site/Brand";
import { adminApi, parseError, setUnauthorizedHandler } from "./api";
import ContentList from "./ContentList";
import Editor from "./Editor";
import ImportWizard from "./ImportWizard";
import Categories from "./Categories";
import SiteContent from "./SiteContent";

const AuthCtx = createContext(null);

const Login = () => {
  const { user, setUser } = useContext(AuthCtx);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const from = useLocation().state?.from || "/admin";
  if (user) return <Navigate to={from} replace />;
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/auth/login", form);
      setUser(data);
      navigate(from, { replace: true });
    } catch (err) {
      setError(parseError(err).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="flex min-h-screen items-center justify-center bg-mist p-4">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 border border-line bg-white p-8" data-testid="admin-login-form">
        <Logo className="h-16 w-auto" />
        <h1 className="text-2xl font-extrabold text-brand">Admin sign in</h1>
        {error && <p role="alert" className="text-sm font-semibold text-[#B42318]" data-testid="admin-login-error">{error}</p>}
        <div className="space-y-2"><label htmlFor="email" className="text-sm font-bold">Email</label><input id="email" type="email" autoComplete="username" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" data-testid="admin-login-email" /></div>
        <div className="space-y-2"><label htmlFor="password" className="text-sm font-bold">Password</label><input id="password" type="password" autoComplete="current-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field" data-testid="admin-login-password" /></div>
        <button type="submit" disabled={busy} className="admin-btn w-full" data-testid="admin-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
        <Link to="/" className="block text-center text-sm text-brand underline">Back to the public site</Link>
      </form>
    </main>
  );
};

const NAV = [["/admin", "Overview", true], ["/admin/stories", "Stories"], ["/admin/events", "Events"], ["/admin/team", "Team"], ["/admin/content", "Website Content"], ["/admin/categories", "Categories"], ["/admin/import", "Import"]];

const Shell = () => {
  const { user, setUser } = useContext(AuthCtx);
  const location = useLocation();
  if (user === undefined) return <p role="status" className="p-8 text-ink-muted">Checking your session…</p>;
  if (!user) return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  const signOut = async () => {
    try {
      await adminApi.post("/auth/logout");
    } catch {
      /* session may already be gone */
    }
    setUser(null);
    toast.success("Signed out");
  };
  return (
    <div className="min-h-screen bg-mist lg:flex">
      <aside className="border-b border-line bg-white p-5 lg:min-h-screen lg:w-64 lg:border-b-0 lg:border-r">
        <Logo className="h-12 w-auto" />
        <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-ink-muted">Content admin</p>
        <nav aria-label="Admin" className="mt-6 flex flex-wrap gap-1 lg:flex-col">
          {NAV.map(([to, label, end]) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `rounded-sm px-3 py-2 text-sm font-bold ${isActive ? "bg-brand text-white" : "text-ink hover:bg-mist"}`} data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>{label}</NavLink>
          ))}
        </nav>
        <div className="mt-6 space-y-2 border-t border-line pt-4 text-sm">
          <p className="truncate text-ink-muted" data-testid="admin-user-email">{user.email}</p>
          <button type="button" onClick={signOut} className="font-bold text-brand underline" data-testid="admin-signout">Sign out</button>
          <a href="/" target="_blank" rel="noopener noreferrer" className="block text-brand underline">View public site</a>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-5 md:p-10"><Outlet /></main>
    </div>
  );
};

const Stat = ({ label, value, to, testId }) => (
  <Link to={to} className="block border border-line bg-white p-6 hover:border-brand" data-testid={testId}>
    <p className="text-4xl font-extrabold text-brand">{value}</p>
    <p className="mt-2 text-sm font-bold text-ink">{label}</p>
  </Link>
);

const Overview = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    adminApi.get("/overview").then((r) => setData(r.data)).catch((e) => setError(parseError(e).message));
  }, []);
  if (error) return <p role="alert" className="text-[#B42318]">{error}</p>;
  if (!data) return <p role="status" className="text-ink-muted">Loading overview…</p>;
  const s = data.stories, e = data.events;
  return (
    <div data-testid="admin-overview">
      <h1 className="text-3xl font-extrabold tracking-tight text-brand">Overview</h1>
      <p className="mt-2 text-sm text-ink-muted">Live counts from the content database. Upcoming means published and not yet ended (Asia/Kolkata); cancelled events are excluded.</p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Published stories" value={s.published} to="/admin/stories" testId="admin-stat-stories-published" />
        <Stat label="Draft stories" value={s.draft} to="/admin/stories" testId="admin-stat-stories-draft" />
        <Stat label="Upcoming published events" value={e.upcomingPublished} to="/admin/events" testId="admin-stat-events-upcoming" />
        <Stat label="Unpublished events" value={e.draft + e.cancelled} to="/admin/events" testId="admin-stat-events-unpublished" />
      </div>
      <ul className="mt-6 space-y-1 text-sm text-ink-muted" data-testid="admin-stat-details">
        <li>Archived stories: {s.archived}</li>
        <li>Unpublished events: {e.draft} draft, {e.cancelled} cancelled</li>
        <li>Past published events: {e.pastPublished}</li>
        <li>Team profiles: {data.team.published} published, {data.team.draft} draft</li>
      </ul>
    </div>
  );
};

export default function AdminApp() {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    document.title = "Admin | BRaIN";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    setUnauthorizedHandler(() => {
      setUser(null);
      toast.error("Your session has expired. Please sign in again.");
    });
    adminApi.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
    return () => robots.remove();
  }, []);
  return (
    <AuthCtx.Provider value={{ user, setUser }}>
      <Toaster richColors position="bottom-right" />
      <Routes>
        <Route path="login" element={<Login />} />
        <Route element={<Shell />}>
          <Route index element={<Overview />} />
          <Route path="import" element={<ImportWizard />} />
          <Route path="categories" element={<Categories />} />
          <Route path="content" element={<SiteContent />} />
          {["stories", "events", "team"].map((k) => [
            <Route key={k} path={k} element={<ContentList kind={k} />} />,
            <Route key={`${k}-edit`} path={`${k}/:id`} element={<Editor kind={k} />} />,
          ])}
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </AuthCtx.Provider>
  );
}
